<template>
  <div class="font-bold mt-4">
    {{ t("account.settings.stripeSubscription.title") }}
  </div>

  <template v-if="account.stripeSubscription">
    <div class="mt-2">
      {{
        t("account.settings.stripeSubscription.status.message", {
          status: stripeSubscriptionStatus,
        })
      }}
    </div>

    <div>
      {{
        t("account.settings.stripeSubscription.invoices", {
          paidCount: stripeSubscriptionPaidInvoicesCount,
          totalCount: stripeSubscriptionInvoicesCount,
        })
      }}
    </div>

    <div>
      {{
        t("account.settings.stripeSubscription.totalAmountRemaining", {
          amount: formatCurrency({
            value: stripeSubscriptionTotalAmountRemaining,
            currencyCode: account.currency || "EUR",
          }),
        })
      }}
    </div>

    <div>
      {{
        t("account.settings.stripeSubscription.totalAmountDue", {
          amount: formatCurrency({
            value: stripeSubscriptionTotalAmountDue,
            currencyCode: account.currency || "EUR",
          }),
        })
      }}
    </div>

    <div class="mt-2">
      <v-btn
        v-if="account.stripeCustomer"
        :href="`${STRIPE_DASHBOARD}/customers/${account.stripeCustomer.id}`"
        target="_blank"
        variant="outlined"
        append-icon="mdi-open-in-new"
        >{{ t("account.settings.stripeSubscription.openStripeDashboardCustomer") }}
      </v-btn>
    </div>
    <div class="mt-2">
      <v-btn
        v-if="account.stripeSubscription"
        :href="`${STRIPE_DASHBOARD}/subscriptions/${account.stripeSubscription.id}`"
        target="_blank"
        variant="outlined"
        append-icon="mdi-open-in-new"
        >{{ t("account.settings.stripeSubscription.openStripeDashboardSubscription") }}
      </v-btn>
    </div>
  </template>

  <template v-else-if="account.subscriptionType === 'stripe'">
    <v-alert
      v-if="stripeSubscriptionCannotBeCreatedReason"
      class="mt-2 mb-2"
      type="warning"
      color="warning"
      variant="tonal"
      density="compact"
      border
    >
      {{ stripeSubscriptionCannotBeCreatedReason }}
    </v-alert>
    <div v-else class="flex items-center mt-4">
      <v-text-field
        v-model="subscriptionStartDate"
        type="date"
        :label="t('account.settings.stripeSubscription.subscriptionStartDate')"
        :rules="[required, dateInPast]"
        style="max-width: 200px"
        :hide-details="true"
        class="mr-4"
        :disabled="creatingStripeSubscription"
      />
      <v-btn
        color="primary"
        variant="outlined"
        :disabled="
          !subscriptionStartDate ||
          dateInPast(subscriptionStartDate) !== true ||
          creatingStripeSubscription
        "
        :loading="creatingStripeSubscription"
        @click="createStripeSubscriptionHandler()"
        >{{ t("account.settings.stripeSubscription.createSubscription") }}</v-btn
      >
    </div>
  </template>

  <template v-else>
    <v-btn
      color="primary"
      variant="outlined"
      class="mt-2"
      @click="setSubscriptionTypeHandler('stripe')"
      >{{ t("account.settings.stripeSubscription.setSubscriptionType") }}</v-btn
    >
  </template>
</template>

<script lang="ts" setup>
import { useCollection } from "@/composables/useFirestore";
import { useI18n } from "@/composables/useI18n";
import { useRules } from "@/composables/useRules";
import { call, db } from "@/firebase";
import { useToast } from "@/store";
import { formatCurrency } from "@/utils/tools";
import type { Account } from "@aircap/common/account";
import {
  hasStripeUnpaidInvoice,
  stripeInvoicesPathFromAccount,
  type StripeInvoice,
} from "@aircap/common/stripe";
import { captureException } from "@sentry/vue";
import { collection, doc, updateDoc } from "firebase/firestore";
import { computed, ref } from "vue";

const props = defineProps<{
  accountId: string;
  account: Account;
}>();

const STRIPE_DASHBOARD = import.meta.env.VITE_STRIPE_DASHBOARD;

const { t } = useI18n();
const { showErrorToast } = useToast();
const { required, dateInPast } = useRules();

const creatingStripeSubscription = ref(false);
const subscriptionStartDate = ref<string | undefined>(
  props.account.swan_account?.IBANCreatedAt?.toDate().toISOString().split("T")[0],
);

const stripeInvoicesRef = computed(() =>
  collection<StripeInvoice>(db, "accounts", props.accountId, stripeInvoicesPathFromAccount),
);
const stripeInvoices = useCollection<StripeInvoice>(stripeInvoicesRef);

const stripeSubscriptionStatus = computed(() => {
  if (!props.account.stripeSubscription) {
    return t("account.settings.stripeSubscription.status.noSubscription");
  }
  return hasStripeUnpaidInvoice(stripeInvoices.value)
    ? t("account.settings.stripeSubscription.status.unpaid")
    : t("account.settings.stripeSubscription.status.active");
});

const stripeSubscriptionInvoicesCount = computed(() => {
  return stripeInvoices.value.length;
});

const stripeSubscriptionPaidInvoicesCount = computed(() => {
  return stripeInvoices.value.filter((invoice) => invoice.status === "paid").length;
});

const stripeSubscriptionTotalAmountDue = computed(() => {
  return stripeInvoices.value.reduce((sum, invoice) => sum + invoice.amount_due / 100, 0);
});

const stripeSubscriptionTotalAmountRemaining = computed(() => {
  return stripeInvoices.value.reduce((sum, invoice) => sum + invoice.amount_remaining / 100, 0);
});

const stripeSubscriptionCannotBeCreatedReason = computed(() => {
  if (!props.account.swan_account)
    return t("account.settings.stripeSubscription.subscriptionCannotBeCreated.accountNotOpened");
  if (!props.account.swan_account.IBAN)
    return t("account.settings.stripeSubscription.subscriptionCannotBeCreated.noIBAN");
  if (!props.account.proAccountPrice)
    return t("account.settings.stripeSubscription.subscriptionCannotBeCreated.noProAccountPrice");
  if (!(
    props.account.swan_account.balances &&
    parseFloat(props.account.swan_account.balances.booked.value) >= props.account.proAccountPrice
  ))
    return t("account.settings.stripeSubscription.subscriptionCannotBeCreated.insufficientBalance");

  return null;
});

const createStripeSubscriptionHandler = async () => {
  if (!subscriptionStartDate.value) return;
  if (dateInPast(subscriptionStartDate.value) !== true) return;
  try {
    console.log("Creating Stripe subscription...");
    creatingStripeSubscription.value = true;
    await call("createStripeSubscription")({
      accountId: props.accountId,
      subscriptionStartDate: new Date(subscriptionStartDate.value).toISOString(),
    });
  } catch (cause) {
    const error = new Error("Error creating Stripe subscription", { cause });
    console.error(error);
    captureException(error);
    showErrorToast();
  } finally {
    creatingStripeSubscription.value = false;
  }
};

const setSubscriptionTypeHandler = async (
  subscriptionType: Required<Account["subscriptionType"]>,
) => {
  try {
    console.log("Setting subscription type...");
    const accountRef = doc<Account>(db, "accounts", props.accountId);
    await updateDoc(accountRef, {
      subscriptionType,
    });
  } catch (cause) {
    const error = new Error("Error setting subscription type", { cause });
    console.error(error);
    captureException(error);
    showErrorToast();
  }
};
</script>
